import React, { useId } from 'react'
import classNames from 'classnames'
import {
  ContentfulCard as ContentfulCardType,
  ContentfulCardStyle,
} from '@reward-platform/contentful'
import { AviosCurrencyBadge, Badge, Heading, Paragraph } from '@alto-avios/alto-ui'
import { useTranslations } from 'next-intl'
import { Card } from '~/components/shared/Card'
import { useLocalisation } from '~/context/localisation'
import { useUser } from '~/context/user'
import { getContentfulImageProps } from '~/utils/assets'
import { createCurrencyFormatter } from '~/utils/formatAmount'
import ContentfulCta from './ContentfulCta'
import { ContentfulCtaEventProps } from './eventTracking'
import styles from './ContentfulCard.module.scss'

type ContentfulCardProps = ContentfulCardType & {
  eventData: ContentfulCtaEventProps
  cardStyle?: ContentfulCardStyle
}

type CardCopyProps = Pick<ContentfulCardType, 'heading' | 'ctaLink'> & {
  label?: React.ReactNode
  subheading?: React.ReactNode
  headingId?: string
  labelId?: string
  subheadingId?: string
  ctaAriaLabel?: string
  eventData: ContentfulCtaEventProps
}

const renderPriceAmount = (chunks: React.ReactNode) => (
  <strong className={styles.priceAmount}>{chunks}</strong>
)

const useCardDetails = ({
  cardStyle,
  label,
  subheading,
  priceAmount,
  collectAmount,
}: Pick<ContentfulCardType, 'label' | 'subheading' | 'priceAmount' | 'collectAmount'> & {
  cardStyle: ContentfulCardStyle
}) => {
  const t = useTranslations('components.contentful_card.labels')
  const { locale } = useLocalisation()
  const { userCurrency } = useUser()

  if (cardStyle !== 'imageOverlay') {
    return { visibleLabel: undefined, visibleSubheading: subheading }
  }

  let visibleLabel: React.ReactNode = label
  if (priceAmount !== undefined) {
    const price = createCurrencyFormatter(userCurrency, locale).format(priceAmount)
    visibleLabel = t.rich('price_from', { price, priceAmount: renderPriceAmount })
  }

  let visibleSubheading: React.ReactNode = subheading
  if (collectAmount !== undefined) {
    visibleSubheading = t('collect_avios', { amount: collectAmount })
  }

  return { visibleLabel, visibleSubheading }
}

const getAviosBadgeContent = (label?: string) => {
  const match = label?.match(/^(.*?)(\d[\d\s,.]*)(.*)$/)
  if (!match) {
    return undefined
  }

  const points = Number(match[2].replace(/\D/g, ''))
  if (!Number.isFinite(points)) {
    return undefined
  }

  return {
    points,
    textStart: match[1].trim() || undefined,
    textEnd: match[3].trim() || undefined,
  }
}

const CardImageBadge = ({
  imageBadge,
  useAviosBadge,
}: {
  imageBadge: NonNullable<ContentfulCardType['imageBadge']>
  useAviosBadge: boolean
}) => {
  const badgeLabel = imageBadge.label ?? ''
  const isAviosBadge = /\bavios\b/i.test(badgeLabel) && !badgeLabel.includes('%')
  const aviosBadgeContent =
    useAviosBadge && isAviosBadge ? getAviosBadgeContent(imageBadge.label) : undefined

  if (aviosBadgeContent) {
    return (
      <AviosCurrencyBadge
        size="sm"
        textStart={aviosBadgeContent.textStart}
        textEnd={aviosBadgeContent.textEnd}
        textStartBold
        textEndBold
        ariaLabel={imageBadge.label}
      >
        {aviosBadgeContent.points}
      </AviosCurrencyBadge>
    )
  }

  return <Badge styleVariant={imageBadge.variant}>{imageBadge.label}</Badge>
}

const CardCopy = ({
  heading,
  label,
  subheading,
  ctaLink,
  headingId,
  labelId,
  subheadingId,
  ctaAriaLabel,
  eventData,
}: CardCopyProps) => (
  <>
    {heading ? (
      <Heading id={headingId} as="h3" size="xs">
        {heading}
      </Heading>
    ) : null}
    {label || subheading ? (
      <div className={styles.details}>
        {label ? (
          <Paragraph id={labelId} size="sm">
            {label}
          </Paragraph>
        ) : null}
        {subheading ? (
          <Paragraph id={subheadingId} size="sm">
            {subheading}
          </Paragraph>
        ) : null}
      </div>
    ) : null}
    <Card.ButtonWrapper>
      {ctaLink ? (
        <ContentfulCta
          {...ctaLink}
          ariaLabel={ctaAriaLabel}
          size="sm"
          eventData={{
            ...eventData,
            linkReference: heading,
          }}
        />
      ) : null}
    </Card.ButtonWrapper>
  </>
)

export const ContentfulCard = ({
  heading,
  label,
  subheading,
  priceAmount,
  collectAmount,
  image,
  bynderImage,
  imageBadge,
  background,
  ctaLink,
  eventData,
  cardStyle = 'tall',
}: ContentfulCardProps) => {
  const cardId = useId()
  const {
    imgSrc: cardImage,
    imgAlt: cardImageAlt,
    imgContentType: cardImageContentType,
    imgFormat: cardImageFormat,
  } = getContentfulImageProps({
    bynderImage,
    image,
    fallbackAlt: heading,
  })
  const hasImageOverlay = cardStyle === 'imageOverlay'
  const { visibleLabel, visibleSubheading } = useCardDetails({
    cardStyle,
    label,
    subheading,
    priceAmount,
    collectAmount,
  })
  const headingId = heading ? `${cardId}-heading` : undefined
  const labelId = visibleLabel ? `${cardId}-label` : undefined
  const subheadingId = visibleSubheading ? `${cardId}-subheading` : undefined
  const descriptionIds = [labelId, subheadingId].filter(Boolean).join(' ') || undefined
  const ctaAriaLabel = ctaLink?.label && heading ? `${ctaLink.label}: ${heading}` : ctaLink?.label

  return (
    <article
      className={classNames(styles.card, {
        [styles.imageOverlay]: hasImageOverlay,
      })}
      aria-labelledby={headingId}
      aria-describedby={descriptionIds}
    >
      <Card backgroundColor={background} layout={hasImageOverlay ? 'imageBackground' : 'default'}>
        {cardImage ? (
          <Card.Image
            src={cardImage}
            alt={cardImageAlt}
            fill={hasImageOverlay}
            width={400}
            height={hasImageOverlay ? 533 : 300}
            contentType={cardImageContentType}
            format={cardImageFormat}
            badge={
              imageBadge ? (
                <CardImageBadge imageBadge={imageBadge} useAviosBadge={hasImageOverlay} />
              ) : undefined
            }
          />
        ) : null}

        <Card.Content overlay={hasImageOverlay}>
          <CardCopy
            heading={heading}
            label={visibleLabel}
            subheading={visibleSubheading}
            ctaLink={ctaLink}
            headingId={headingId}
            labelId={labelId}
            subheadingId={subheadingId}
            ctaAriaLabel={ctaAriaLabel}
            eventData={eventData}
          />
        </Card.Content>
      </Card>
    </article>
  )
}
